import { AuthSubscriptions } from '@hefe/auth';
import { t } from '@hefe/i18n';
import { BasketballTeamSettings } from '@hefe/models';
import {
  BasketballTeamSettingsData,
  BasketballTeamsSlice,
  currentUserSelector,
  userActiveBasketballCoachTeamId,
} from '@hefe/store';
import { clubHasSubscription } from '@hefe/utils';
import {
  Checkbox,
  Col,
  Descriptions,
  Row,
  Space,
  Spin,
  Typography,
} from 'antd';
import { produce } from 'immer';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';

export default function TeamSettingsPage() {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);

  const user = useSelector(currentUserSelector);
  const activeCoachTeamId = useSelector(userActiveBasketballCoachTeamId);
  const team = useSelector(
    BasketballTeamsSlice.basketballTeamSelector(activeCoachTeamId ?? 0)
  );

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = () => {
    if (!activeCoachTeamId) {
      return;
    }

    setLoading(true);
    dispatch(BasketballTeamsSlice.fetchBasketballTeam(activeCoachTeamId))
      .unwrap()
      .finally(() => setLoading(false));
  };

  const saveSetting = (key: any, value: any) => {
    if (!team || !team.settings) {
      return;
    }

    setSaving(true);

    const data: any = {
      settings: produce(
        team.settings as BasketballTeamSettings,
        (draft: BasketballTeamSettings) => {
          // set draft key - sub keys are divided by dots
          _.set(draft, key, value);
        }
      ),
    };

    dispatch(
      BasketballTeamsSlice.updateBasketballTeamSettings({
        settings: data,
        teamId: team.id,
      })
    )
      .unwrap()
      .finally(() => setSaving(false));
  };

  const renderDescriptionLabel = (title: string, subtitle?: string) => {
    return (
      <Space direction="vertical" size={[0, 0]}>
        {title.length > 0 && <Typography.Text strong>{title}</Typography.Text>}
        {subtitle && subtitle.length > 0 && (
          <Typography.Text style={{ fontSize: 12 }}>{subtitle}</Typography.Text>
        )}
      </Space>
    );
  };

  const renderSettingsRecursive = (
    setting: BasketballTeamSettingsData.TeamSetting,
    parentKey?: string
  ) => {
    const key = parentKey ? `${parentKey}.${setting.key}` : setting.key;

    if (!clubHasSubscription(AuthSubscriptions[setting.subscription], user)) {
      return null;
    }

    if (setting.children) {
      const returnValue = [];
      returnValue.push(
        <Descriptions.Item
          key={setting.key}
          label={renderDescriptionLabel(
            t(`setting_texts.${key}.title`),
            t(`setting_texts.${key}.description`)
          )}
        >
          <Typography.Text></Typography.Text>
        </Descriptions.Item>
      );
      returnValue.push(
        setting.children.map((child): any => {
          return renderSettingsRecursive(child, key);
        })
      );
      return returnValue;
    } else {
      return (
        <Descriptions.Item
          key={setting.key}
          label={renderDescriptionLabel(
            t(`setting_texts.${key}.title`),
            t(`setting_texts.${key}.description`)
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Checkbox
              disabled={loading || isSaving}
              checked={_.get(team?.settings, key) ?? false}
              onChange={(e) => saveSetting(key, e.target.checked)}
            />
          )}
        </Descriptions.Item>
      );
    }
  };

  return (
    <Row justify="start">
      <Col xs={24} md={12}>
        <Descriptions bordered column={1}>
          {BasketballTeamSettingsData.SETTINGS.map((item) => {
            return renderSettingsRecursive(item);
          })}
          {}
        </Descriptions>
      </Col>
    </Row>
  );
}
